/**
 * One branch per workflow node type: 45 of them, each a handful of literal defaults and no logic.
 * The length is the number of node types, not complexity, and splitting the table across files
 * would only make a reader hunt for the type they came for. It shrinks per node type when the
 * element-plugin work (ADR 0004) gives each node its own slice to declare its defaults in.
 */
import {
  ComparisonOperator,
  LogicalType,
  PrintLayout,
  QueryNodeType,
  type WorkflowNodeType,
} from '@blaze-shared/api-contracts-domain';
import { BULK_ACTION_IDS_PROP } from '@blaze-shared/elements-domain';
import {
  type AggregateWorkflowNode,
  type AiApiWorkflowNode,
  type ApiCallWorkflowNode,
  type AssignWorkflowNode,
  type BreakWorkflowNode,
  type CloseDialogWorkflowNode,
  type ComputeWorkflowNode,
  type ConfirmDialogWorkflowNode,
  type CopyToClipboardWorkflowNode,
  DEFAULT_PDF_HEADER_FOOTER_CONFIG,
  type DelayWorkflowNode,
  type DeleteWorkflowNode,
  type DownloadWorkflowNode,
  type ExceptWorkflowNode,
  type ExecuteJavascriptWorkflowNode,
  type ExecuteWorkflowNode,
  type ExpandWorkflowNode,
  type ExportToXlsxWorkflowNode,
  type FilterWorkflowNode,
  type ForEachWorkflowNode,
  type GetUserLocationWorkflowNode,
  type GetWorkflowNode,
  type IfWorkflowNode,
  type ImportFileWorkflowNode,
  type InsertWorkflowNode,
  type JoinWorkflowNode,
  type LookupWorkflowNode,
  type MergeWorkflowNode,
  type NavigateWorkflowNode,
  type OpenDialogWorkflowNode,
  type PivotWorkflowNode,
  type PrintToPdfWorkflowNode,
  type ReadCsvDataWorkflowNode,
  type ReadJsonDataWorkflowNode,
  type ReadXlsxWorkflowNode,
  type SaveFileWorkflowNode,
  type SelectColumnsWorkflowNode,
  type SendPushNotificationWorkflowNode,
  type SortWorkflowNode,
  type StoreVariableWorkflowNode,
  type TerminateWorkflowNode,
  type ToJsonWorkflowNode,
  type TransposeWorkflowNode,
  type UnionWorkflowNode,
  type UpdateWorkflowNode,
  type WhileWorkflowNode,
  type WorkflowNode,
  type WorkflowNodeBase,
} from '@blaze-shared/workflow-domain';

/**
 * What a new node needs to know about where it is being added.
 *
 * Only `Delete` and `Update` read any of it: what they default to depends on whether they run
 * inside a bulk action, on the server, and on what feeds them.
 */
export interface NewNodeContext {
  /** The dataset a bulk action's node writes to; unknown for a node added anywhere else. */
  datasetId?: string;
  id: string;
  isBulkActionNode: boolean;
  /** Whether the node is fed by a `Get`, which already narrowed the rows to act on. */
  isPreviousNodeGetNode: boolean;
  isServerWorkflow: boolean;
}

/** The blank node the editor puts on the canvas, before the author configures anything. */
export function createWorkflowNode(type: WorkflowNodeType, context: NewNodeContext): WorkflowNode {
  const base = getWorkflowNodeBase(type, context.id);

  switch (type) {
    case 'Aggregate': {
      const node: AggregateWorkflowNode = {
        ...base,
        nodeType: 'Aggregate',
        functionDefs: [],
        groupByColumns: [],
      };
      return node;
    }
    case 'AI': {
      const node: AiApiWorkflowNode = {
        ...base,
        nodeType: 'AI',
        actionName: '',
        connectorId: '',
      };
      return node;
    }
    case 'ApiCall': {
      const node: ApiCallWorkflowNode = {
        ...base,
        nodeType: 'ApiCall',
        actionName: '',
        connectorId: '',
        parameters: {},
        runOncePerInput: false,
      };
      return node;
    }
    case 'Assign': {
      const node: AssignWorkflowNode = {
        ...base,
        nodeType: 'Assign',
        isFormulaValue: false,
        outputName: '',
        scope: 'Workflow',
        value: '',
      };
      return node;
    }
    case 'Break': {
      const node: BreakWorkflowNode = {
        ...base,
        nodeType: 'Break',
      };
      return node;
    }
    case 'CloseDialog': {
      const node: CloseDialogWorkflowNode = {
        ...base,
        nodeType: 'CloseDialog',
        dialogId: '',
        passDataAsInput: false,
      };
      return node;
    }
    case 'Compute': {
      const node: ComputeWorkflowNode = {
        ...base,
        nodeType: 'Compute',
        value: '',
        name: '',
      };
      return node;
    }
    case 'ConfirmDialog': {
      const node: ConfirmDialogWorkflowNode = {
        ...base,
        nodeType: 'ConfirmDialog',
        buttons: [{ id: crypto.randomUUID(), label: 'OK', action: 'continue' }],
        message: '',
        stopExecution: false,
        title: '',
      };
      return node;
    }
    case 'CopyToClipboard': {
      const node: CopyToClipboardWorkflowNode = {
        ...base,
        nodeType: 'CopyToClipboard',
        column: '',
        condition: '',
      };
      return node;
    }
    case 'Delay': {
      const node: DelayWorkflowNode = {
        ...base,
        nodeType: 'Delay',
        delayMs: 1000,
      };
      return node;
    }
    case 'Delete':
      return createDeleteNodeDefaults(base as WorkflowNodeBase<'Delete'>, context);
    case 'Download': {
      const node: DownloadWorkflowNode = {
        ...base,
        nodeType: 'Download',
        createZipArchive: false,
        downloadToBrowser: true,
        fileName: '',
        fileNameDisplayInfos: [],
        url: '',
      };
      return node;
    }
    case 'ExecuteJavascript': {
      const node: ExecuteJavascriptWorkflowNode = {
        ...base,
        nodeType: 'ExecuteJavascript',
        value: '',
      };
      return node;
    }
    case 'Except': {
      const node: ExceptWorkflowNode = {
        ...base,
        nodeType: 'Except',
        targetNodeId: '',
        condition: '',
      };
      return node;
    }
    case 'ExecuteWorkflow': {
      const node: ExecuteWorkflowNode = {
        ...base,
        nodeType: 'ExecuteWorkflow',
        workflowId: '',
        filterInputName: BULK_ACTION_IDS_PROP,
        hasActionNode: false,
        workflowType: 'Regular',
        assignments: [],
        outputVariables: {},
        mergeWithInput: false,
      };
      return node;
    }
    case 'Expand': {
      const node: ExpandWorkflowNode = {
        ...base,
        column: '',
        keepCurrentInput: false,
        nodeType: 'Expand',
      };
      return node;
    }
    case 'Filter': {
      const node: FilterWorkflowNode = {
        ...base,
        nodeType: 'Filter',
        condition: '',
        formula: '',
      };
      return node;
    }
    case 'Get': {
      const node: GetWorkflowNode = {
        ...base,
        nodeType: 'Get',
        query: {
          nodeType: QueryNodeType.Logical,
          logicalType: LogicalType.And,
          children: [
            {
              nodeType: QueryNodeType.Comparison,
              column: '',
              value: '',
              conditionId: '',
              isFormulaValue: false,
              operator: ComparisonOperator.Eq,
              isOptional: false,
              displayInfos: [],
            },
          ],
        },
        ignoreExternalAccountFiltering: false,
        isFormulaCondition: false,
        projectColumns: [],
        queryConditions: [],
        queryLogicalOp: LogicalType.And,
        sorts: [],
      };
      return node;
    }
    case 'ExportToXlsx': {
      const node: ExportToXlsxWorkflowNode = {
        ...base,
        nodeType: 'ExportToXlsx',
        displayInfos: [],
        fileName: '',
        isPublic: false,
        localizeTimestamps: false,
        sheetsToColumnsMappings: {},
      };
      return node;
    }
    case 'ForEach': {
      const node: ForEachWorkflowNode = {
        ...base,
        nodeType: 'ForEach',
        blockStartNodeId: '',
        displayInfos: [],
      };
      return node;
    }
    case 'GetUserLocation': {
      const node: GetUserLocationWorkflowNode = {
        ...base,
        nodeType: 'GetUserLocation',
      };
      return node;
    }
    case 'If': {
      const node: IfWorkflowNode = {
        ...base,
        nodeType: 'If',
        checkRows: 'Any',
        condition: '',
        targetIdForFalse: '',
        targetIdForTrue: '',
      };
      return node;
    }
    case 'ImportFile': {
      const node: ImportFileWorkflowNode = {
        ...base,
        nodeType: 'ImportFile',
        displayInfos: [],
        fileName: '',
        fileNameDisplayInfos: [],
        fileUrl: '',
      };
      return node;
    }
    case 'Insert': {
      const node: InsertWorkflowNode = {
        ...base,
        nodeType: 'Insert',
        assignments: [],
        runOnce: true,
      };
      return node;
    }
    case 'Join': {
      const node: JoinWorkflowNode = {
        ...base,
        condition: '',
        conditionList: [],
        isFormulaCondition: false,
        joinOperator: LogicalType.And,
        joinType: 'Inner',
        nodeType: 'Join',
        targetId: '',
      };
      return node;
    }
    case 'Lookup': {
      const node: LookupWorkflowNode = {
        ...base,
        nodeType: 'Lookup',
        datasetId: '',
        joinCondition: {
          column: '',
          value: '',
          isFormulaValue: false,
          operator: ComparisonOperator.Eq,
        },
        subDataset: '',
        version: 2,
      };
      return node;
    }
    case 'Merge': {
      const node: MergeWorkflowNode = {
        ...base,
        nodeType: 'Merge',
        assignments: [],
        version: 2,
      };
      return node;
    }
    case 'Navigate': {
      const node: NavigateWorkflowNode = {
        ...base,
        nodeType: 'Navigate',
        navigateType: 'Page',
        passDataAsInput: false,
        target: '',
      };
      return node;
    }
    case 'OpenDialog': {
      const node: OpenDialogWorkflowNode = {
        ...base,
        nodeType: 'OpenDialog',
        dialogId: '',
        passDataAsInput: false,
        assignments: [],
      };
      return node;
    }
    case 'Pivot': {
      const node: PivotWorkflowNode = {
        ...base,
        nodeType: 'Pivot',
        aggregateFunction: { name: '', column: '' },
        groupByColumns: [],
        pivotColumn: '',
      };
      return node;
    }
    case 'PrintToPdf': {
      const node: PrintToPdfWorkflowNode = {
        ...base,
        nodeType: 'PrintToPdf',
        displayInfos: [],
        encodeAsBase64: false,
        fileName: '',
        footer: { ...DEFAULT_PDF_HEADER_FOOTER_CONFIG },
        header: { ...DEFAULT_PDF_HEADER_FOOTER_CONFIG },
        pageContextInputName: undefined,
        printLayout: PrintLayout.Portrait,
        pageId: '',
      };
      return node;
    }
    case 'ReadCsvData': {
      const node: ReadCsvDataWorkflowNode = {
        ...base,
        nodeType: 'ReadCsvData',
        csvFileUrl: '',
      };
      return node;
    }
    case 'ReadJsonData': {
      const node: ReadJsonDataWorkflowNode = {
        ...base,
        nodeType: 'ReadJsonData',
        jsonFileUrl: '',
      };
      return node;
    }
    case 'ReadXlsx': {
      const node: ReadXlsxWorkflowNode = {
        ...base,
        nodeType: 'ReadXlsx',
        xlsxFileUrl: '',
        localizeTimestamps: false,
      };
      return node;
    }
    case 'SaveFile': {
      const node: SaveFileWorkflowNode = {
        ...base,
        nodeType: 'SaveFile',
        chooseColumns: false,
        columns: [],
        displayInfos: [],
        filename: '',
        fileType: 'Csv',
        isPublic: false,
        localizeTimestamps: false,
      };
      return node;
    }
    case 'SelectColumns': {
      const node: SelectColumnsWorkflowNode = {
        ...base,
        nodeType: 'SelectColumns',
        columns: [],
      };
      return node;
    }
    case 'SendPushNotification': {
      const node: SendPushNotificationWorkflowNode = {
        ...base,
        body: '',
        bodyDisplayInfos: [],
        icon: undefined,
        nodeType: 'SendPushNotification',
        pageId: '',
        recipients: '',
        recipientsDisplayInfos: [],
        title: '',
        titleDisplayInfos: [],
        url: '',
      };
      return node;
    }
    case 'Sort': {
      const node: SortWorkflowNode = {
        ...base,
        nodeType: 'Sort',
        columns: [],
      };
      return node;
    }
    case 'StoreVariable': {
      const node: StoreVariableWorkflowNode = {
        ...base,
        nodeType: 'StoreVariable',
        name: '',
        scope: 'User',
        value: '',
      };
      return node;
    }
    case 'Terminate': {
      const node: TerminateWorkflowNode = {
        ...base,
        nodeType: 'Terminate',
      };
      return node;
    }
    case 'ToJson': {
      const node: ToJsonWorkflowNode = {
        ...base,
        nodeType: 'ToJson',
        assignments: [],
        input: null,
        name: '',
      };
      return node;
    }
    case 'Transpose': {
      const node: TransposeWorkflowNode = {
        ...base,
        nodeType: 'Transpose',
        transposeColumn: '',
        propertyColumnName: '',
      };
      return node;
    }
    case 'Union': {
      const node: UnionWorkflowNode = {
        ...base,
        nodeType: 'Union',
        projectColumns: [],
        removeDuplicates: false,
        targetNodeIds: [],
      };
      return node;
    }
    case 'Update':
      return createUpdateNodeDefaults(base as WorkflowNodeBase<'Update'>, context);
    case 'While': {
      const node: WhileWorkflowNode = {
        ...base,
        nodeType: 'While',
        blockStartNodeId: '',
        checkBefore: true,
        condition: '',
      };
      return node;
    }
    default:
      throw new Error(`Generation of node type: "${type}" is not supported`);
  }
}

export const getWorkflowNodeBase = <T extends WorkflowNodeType = WorkflowNodeType>(
  nodeType: T,
  id: string,
): WorkflowNodeBase<T> => ({ id, nodeType, nextNodeId: '' });

/**
 * A delete node acts on one row unless it is the action of a bulk action, in which case it deletes
 * the selection — and asks first, with a message naming how many rows that is. A server workflow
 * has nobody to ask.
 */
function createDeleteNodeDefaults(
  base: WorkflowNodeBase<'Delete'>,
  { datasetId, isBulkActionNode, isPreviousNodeGetNode, isServerWorkflow }: NewNodeContext,
): DeleteWorkflowNode {
  const askForConfirmation = !isServerWorkflow;

  if (!isBulkActionNode || isPreviousNodeGetNode) {
    return { ...base, askForConfirmation, queryConditions: [], runOnce: true };
  }
  return {
    ...base,
    askForConfirmation,
    confirmationMessage: `$$input1.length ? 'Do you want to delete the '+$$input1.length+' selected rows?' : 'Do you want to delete all the rows?'`,
    currentRow: true,
    datasetId,
    displayInfos: [
      {
        display: 'Do you want to delete the ',
        formulaName: '',
        isAutoCompleted: false,
        value: 'Do you want to delete the ',
      },
      {
        display: 'selectedRows count',
        formulaName: '',
        isAutoCompleted: true,
        value: '$$input1.length',
      },
      {
        display: ' selected rows?',
        formulaName: '',
        isAutoCompleted: false,
        value: ' selected rows?',
      },
    ],
    queryConditions: [],
    runOnce: false,
  };
}

/**
 * An update node acts on one row unless it is the action of a bulk action, in which case it
 * updates the current row of each selected row in turn.
 */
function createUpdateNodeDefaults(
  base: WorkflowNodeBase<'Update'>,
  { datasetId, isBulkActionNode }: NewNodeContext,
): UpdateWorkflowNode {
  if (!isBulkActionNode) {
    return {
      ...base,
      assignments: [],
      chooseColumns: false,
      columns: [],
      condition: null,
      description: null,
      dialogId: null,
      errorDisplayInfos: [],
      errorMessage: null,
      onError: 'Throw',
      queryConditions: [],
      runOnce: true,
      successDisplayInfos: [],
      successMessage: null,
      upsert: false,
    };
  }
  return {
    ...base,
    assignments: [],
    currentRow: true,
    datasetId,
    queryConditions: [],
    runOnce: false,
    upsert: false,
  };
}
